// Composer Studio — 특정 바인딩이 위치한 셀/노드를 캔버스에 outline으로 하이라이트한다.
// SelectionOverlay 패턴 복제(맨 위 오버레이라 이웃에 안 덮임). 소비처는 tone으로 갈린다:
//   R2 계산 입력 "기존 값" 콤보 호버(amber) · R3 픽 모드 호버(valid/invalid) ·
//   식별 키 공유 자리 강조(shared — sharedKeyHighlight.ts).
// 레이아웃 중립(absolute overlay + pointer-events:none)이라 캔버스 흐름을 건드리지 않는다.

import { getCellRect, getNodeRect, type Rect } from './canvasRect';

/**
 * availableBinding id(`cell:tableId:row:col` 또는 `node:nodeId`)를 canvas 상대 rect로 환산.
 * 못 찾으면 null. tableId 자체엔 ':'가 없다는 전제(node id는 `${kind}-${n}` 형식)로 뒤 2개를 row/col로 파싱.
 */
function rectForSourceId(canvas: HTMLElement, sourceId: string): Rect | null {
  if (sourceId.startsWith('cell:')) {
    const rest = sourceId.slice('cell:'.length);
    const colSep = rest.lastIndexOf(':');
    if (colSep === -1) return null;
    const beforeCol = rest.slice(0, colSep);
    const rowSep = beforeCol.lastIndexOf(':');
    if (rowSep === -1) return null;
    const tableId = beforeCol.slice(0, rowSep);
    const row = Number(beforeCol.slice(rowSep + 1));
    const col = Number(rest.slice(colSep + 1));
    if (!tableId || Number.isNaN(row) || Number.isNaN(col)) return null;
    return getCellRect(canvas, tableId, row, col);
  }
  if (sourceId.startsWith('node:')) {
    const nodeId = sourceId.slice('node:'.length);
    return nodeId ? getNodeRect(canvas, nodeId) : null;
  }
  return null;
}

/** 하이라이트 색 톤 — amber: R2 콤보 호버(기본), valid: 픽 가능(바인딩 있음, 파랑),
 *  invalid: 픽 불가(바인딩 없음, 빨강 dashed),
 *  shared: 식별 키를 공유하는 다른 자리(rose — teal은 캔버스에서 묻혀 가시성 부족, 2026-09-03 교체.
 *  invalid(red-500 2px)와는 색조·두께로 갈리고, 픽 모드와 키 포커스는 동시 성립하지 않는다). */
type HighlightTone = 'amber' | 'valid' | 'invalid' | 'shared';

const TONE_STYLE: Record<HighlightTone, { outline: string; background: string }> = {
  amber: { outline: '2px solid #f59e0b', background: 'rgba(245, 158, 11, 0.12)' },
  valid: { outline: '2px solid #4f46e5', background: 'rgba(79, 70, 229, 0.12)' }, // brand-600
  invalid: { outline: '2px dashed #ef4444', background: 'rgba(239, 68, 68, 0.10)' },
  shared: { outline: '3px dashed #e11d48', background: 'rgba(225, 29, 72, 0.14)' }, // rose-600
};

export function ComputeSourceHighlightOverlay({
  canvas,
  sourceId,
  tone = 'amber',
}: {
  canvas: HTMLElement | null;
  sourceId: string | null;
  tone?: HighlightTone;
}) {
  if (!canvas || !sourceId) return null;
  const r = rectForSourceId(canvas, sourceId);
  if (!r) return null;
  const { outline, background } = TONE_STYLE[tone];
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 39 }}>
      <div
        style={{
          position: 'absolute',
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          outline,
          outlineOffset: '1px',
          background,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
